import React, { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, UserX, Phone, Cake, Mail, Loader2, Search, FileCheck2, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import StaffProfileDialog from "@/components/app/StaffProfileDialog";
import UserAvatar from "@/components/app/UserAvatar";

const ROLE_OPTIONS = [
  { key: "housekeeping", label: "Housekeeping" },
  { key: "maintenance", label: "Maintenance" },
  { key: "supervisor", label: "Supervisor" },
  { key: "general_manager", label: "General Manager" },
  { key: "property_admin", label: "Property Admin" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function PendingApprovals() {
  const [tab, setTab] = useState("signups"); // "signups" | "verifications"
  const [items, setItems] = useState([]);
  const [verifyItems, setVerifyItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ role: "housekeeping", property_ids: [], date_of_joining: "", department: "", employment_status: "probation" });
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [openProfile, setOpenProfile] = useState(null);
  const [verifyBusyId, setVerifyBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, v, p, d] = await Promise.all([
        api.get("/users/pending-approvals"),
        api.get("/users/pending-verification"),
        api.get("/properties"),
        api.get("/departments"),
      ]);
      setItems(u.data);
      setVerifyItems(v.data);
      setProperties(p.data);
      setDepartments(d.data);
    } catch (e) {
      logger.error("Failed to load pending approvals", e);
      toast.error("Could not load pending approvals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openApprove = (u) => {
    setApproveFor(u);
    setApproveForm({
      role: u.role || "housekeeping",
      property_ids: u.property_ids || [],
      date_of_joining: u.date_of_joining || "",
      department: u.department || "",
      employment_status: u.employment_status || "probation",
    });
  };

  const submitApprove = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${approveFor.id}/approve`, {
        role: approveForm.role,
        property_ids: approveForm.property_ids,
        date_of_joining: approveForm.date_of_joining || null,
        department: approveForm.department || null,
        employment_status: approveForm.employment_status,
      });
      toast.success(`${approveFor.name} approved — they can now sign in.`);
      setApproveFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${rejectFor.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success(`${rejectFor.name}'s signup was rejected.`);
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };

  const togglePropertyId = (pid) => {
    setApproveForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(pid)
        ? f.property_ids.filter((x) => x !== pid)
        : [...f.property_ids, pid],
    }));
  };

  const quickVerify = async (u) => {
    setVerifyBusyId(u.id);
    try {
      await api.post(`/users/${u.id}/profile/verify`);
      toast.success(`${u.name} verified — they can now use the app.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifyBusyId(null);
    }
  };

  const filtered = items.filter((u) => {
    const q = query.toLowerCase();
    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const filteredVerify = verifyItems.filter((u) => {
    const q = query.toLowerCase();
    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Team</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Pending Approvals</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-lg">
          Two-stage onboarding queue. Approve new signups so they can sign in, then verify their KYC documents to unlock full app access.
        </p>
      </div>

      {/* Tab toggle */}
      <div className="inline-flex rounded-md border border-zinc-200 p-0.5" role="tablist">
        <button
          type="button"
          onClick={() => setTab("signups")}
          className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded inline-flex items-center gap-2 ${tab === "signups" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
          data-testid="tab-signups"
        >
          Signups
          <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${tab === "signups" ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-700"}`}>{items.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("verifications")}
          className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded inline-flex items-center gap-2 ${tab === "verifications" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
          data-testid="tab-verifications"
        >
          Document verifications
          <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${tab === "verifications" ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-700"}`}>{verifyItems.length}</span>
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="pl-9"
          data-testid="pending-approvals-search"
        />
      </div>

      {tab === "signups" && (() => {
        if (loading) {
          return <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
        }
        if (filtered.length === 0) {
          return (
            <div className="bg-white border border-zinc-200 rounded-md p-10 text-center" data-testid="pending-approvals-empty">
              <UserCheck className="w-8 h-8 mx-auto text-zinc-300" strokeWidth={1.5} />
              <div className="mt-2 text-zinc-500 text-sm">No signups awaiting approval right now.</div>
            </div>
          );
        }
        return (
          <div className="space-y-3">
            {filtered.map((u) => (
              <div key={u.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`pending-row-${u.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl font-bold tracking-tight">{u.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Submitted {fmtDate(u.created_at)}
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-zinc-700">
                      <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-zinc-400" /> {u.email}</span>
                      <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-zinc-400" /> {u.profile?.phone || "—"}</span>
                      <span className="flex items-center gap-2"><Cake className="w-3.5 h-3.5 text-zinc-400" /> {u.profile?.date_of_birth || "—"}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      onClick={() => { setRejectFor(u); setRejectReason(""); }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`reject-${u.id}`}
                    >
                      <UserX className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                    <Button
                      onClick={() => openApprove(u)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approve-${u.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {tab === "verifications" && (() => {
        if (loading) {
          return <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
        }
        if (filteredVerify.length === 0) {
          return (
            <div className="bg-white border border-zinc-200 rounded-md p-10 text-center" data-testid="pending-verifications-empty">
              <FileCheck2 className="w-8 h-8 mx-auto text-zinc-300" strokeWidth={1.5} />
              <div className="mt-2 text-zinc-500 text-sm">No documents are awaiting verification right now.</div>
            </div>
          );
        }
        return (
          <div className="space-y-3">
            {filteredVerify.map((u) => {
              const docs = u.profile || {};
              return (
                <div key={u.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`verify-row-${u.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar user={u} size={48} />
                      <div className="min-w-0">
                        <div className="font-display text-xl font-bold tracking-tight">{u.name}</div>
                        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">
                          {u.role?.replace("_", " ") || "—"} · {u.email}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                          {["id_proof_front_url", "id_proof_back_url", "address_proof_front_url", "address_proof_back_url"].map((k) => (
                            <span
                              key={k}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${docs[k] ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}
                            >
                              {docs[k] ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                              {k.replace(/_url$/, "").replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        onClick={() => setOpenProfile(u.id)}
                        data-testid={`verify-view-${u.id}`}
                      >
                        Review profile
                      </Button>
                      <Button
                        onClick={() => quickVerify(u)}
                        disabled={verifyBusyId === u.id}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`verify-now-${u.id}`}
                      >
                        {verifyBusyId === u.id ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
                        Verify
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Approve dialog */}
      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {approveFor?.name}</DialogTitle>
            <DialogDescription>
              Set their role and property access. They'll get a notification and can sign in immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={approveForm.role} onValueChange={(v) => setApproveForm({ ...approveForm, role: v })}>
                  <SelectTrigger data-testid="approve-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employment status</Label>
                <Select value={approveForm.employment_status} onValueChange={(v) => setApproveForm({ ...approveForm, employment_status: v })}>
                  <SelectTrigger data-testid="approve-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="probation">Probation</SelectItem>
                    <SelectItem value="temporary">Temporary</SelectItem>
                    <SelectItem value="permanent">Permanent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Department</Label>
                <Select value={approveForm.department || "__none"} onValueChange={(v) => setApproveForm({ ...approveForm, department: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="approve-department"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Not set —</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date of joining</Label>
                <Input
                  type="date"
                  value={approveForm.date_of_joining}
                  onChange={(e) => setApproveForm({ ...approveForm, date_of_joining: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="approve-doj"
                />
              </div>
            </div>
            <div>
              <Label>Property access (optional)</Label>
              <div className="mt-2 space-y-1 max-h-40 overflow-auto border border-zinc-200 rounded p-2">
                {properties.length === 0 ? (
                  <div className="text-xs text-zinc-500 px-1 py-2">No properties available.</div>
                ) : properties.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={approveForm.property_ids.includes(p.id)}
                      onChange={() => togglePropertyId(p.id)}
                      data-testid={`approve-property-${p.id}`}
                    />
                    <span>{p.name}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400">{p.location}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Leave empty if they aren't tied to a property yet — you can change this anytime from the Staff page.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserCheck className="w-4 h-4 mr-1.5" />}
              Approve &amp; activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {rejectFor?.name}</DialogTitle>
            <DialogDescription>
              They will see this reason if they try to sign in. They won't be able to access the app unless you re-approve them later.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Not a current employee"
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button onClick={submitReject} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="reject-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserX className="w-4 h-4 mr-1.5" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff profile dialog — used by the verifications tab to review uploaded documents */}
      <StaffProfileDialog
        userId={openProfile}
        open={!!openProfile}
        onOpenChange={(o) => { if (!o) { setOpenProfile(null); load(); } }}
      />
    </div>
  );
}
